import { Container } from '@/components/ui/Container';
import { WHY_HIGHLIGHT, WHY_POINTS, WHY_SECTION, type WhyPoint } from '@/data/homeContent';

/**
 * The four reasons, and the merchant count beside them.
 *
 * "Made for India" gets the tricolour roundel rather than a lucide glyph -
 * a flag is the one thing in this section that is not a generic idea, and the
 * saffron in it is the only place on the page the accent appears at full
 * strength.
 */
function TricolourRoundel() {
  return (
    <svg viewBox="0 0 24 24" className="size-9" role="img" aria-hidden focusable="false">
      <defs>
        <clipPath id="home-tricolour-clip">
          <circle cx="12" cy="12" r="12" />
        </clipPath>
      </defs>
      <g clipPath="url(#home-tricolour-clip)">
        <rect x="0" y="0" width="24" height="8" fill="var(--color-saffron-500)" />
        <rect x="0" y="8" width="24" height="8" fill="#ffffff" />
        <rect x="0" y="16" width="24" height="8" fill="var(--color-brand-500)" />
        <circle cx="12" cy="12" r="2.6" fill="none" stroke="#1e3a8a" strokeWidth="0.9" />
      </g>
      <circle cx="12" cy="12" r="11.5" fill="none" stroke="var(--color-line)" strokeWidth="1" />
    </svg>
  );
}

function PointIcon({ icon }: { icon: WhyPoint['icon'] }) {
  if (icon === 'tricolour') return <TricolourRoundel />;

  const Icon = icon;
  return (
    <span className="grid size-9 place-items-center rounded-full bg-brand-500">
      <Icon className="size-4.5 text-white" aria-hidden />
    </span>
  );
}

export function WhyNeonCarts() {
  const HighlightIcon = WHY_HIGHLIGHT.icon;

  return (
    <section aria-labelledby="home-why" className="pb-8 pt-6 sm:pb-10 sm:pt-8">
      <Container>
        <h2
          id="home-why"
          className="text-balance text-2xl font-bold tracking-tight text-ink sm:text-3xl"
        >
          {WHY_SECTION.heading}
        </h2>

        {/* Four points and the count in one row from `lg`, the way the design
            shows them. That row gives each point about 115px of text next to
            its icon, so the copy is set at the same small, tight sizes the
            feature cards use rather than stepping up at `sm`. */}
        <div className="mt-5 grid gap-5 sm:mt-6 lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-6">
          <ul className="grid gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_POINTS.map((point) => (
              <li key={point.title} className="flex items-start gap-2.5">
                <span className="shrink-0">
                  <PointIcon icon={point.icon} />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold leading-snug tracking-tight text-ink">
                    {point.title}
                  </h3>
                  <p className="mt-1 hyphens-auto text-justify text-xs leading-relaxed text-muted">
                    {point.description}
                  </p>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50/80 p-4 shadow-tile">
            <div className="min-w-0">
              <p className="text-2xl font-bold tracking-tight text-brand-600 sm:text-3xl">
                {WHY_HIGHLIGHT.value}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-muted">{WHY_HIGHLIGHT.caption}</p>
            </div>
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-white/80">
              <HighlightIcon className="size-5 text-brand-500" aria-hidden />
            </span>
          </div>
        </div>
      </Container>
    </section>
  );
}
